// src/features/tasks/components/TaskDetailsPage/AddEquipmentDialog.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MeetingRoom as RoomIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEquipment } from '../../../equipment/hooks/useEquipment';
import { getEquipmentImageByModel } from '../../../equipment/utils/getEquipmentImage';
import { RELATION_TYPES } from '../../../equipment/utils/enums';
import RotationOptionsEditor from './RotationOptionsEditor';

export default function AddEquipmentDialog({ 
  open, 
  onClose, 
  onSubmit,
  linkedEquipmentIds = [], // Array of equipment IDs already linked to task
  submitLoading = false,
  submitError = null,
}) {
  const { t } = useTranslation(['tasks', 'equipment', 'rooms', 'common']);
  const { equipment, loading: equipmentLoading, fetchDepartmentEquipment } = useEquipment();
  
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [relationType, setRelationType] = useState('KONSTANS');
  const [notes, setNotes] = useState('');
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [rotationEnabled, setRotationEnabled] = useState(false);
  const [rotationLabel, setRotationLabel] = useState('');
  const [rotationOptions, setRotationOptions] = useState([]);

  useEffect(() => {
    if (open) {
      // Keep old type and notes if the user forgot to add a equipment
      setSelectedEquipments([]);
      setExpandedRoom(null);
      fetchDepartmentEquipment(false); // Only active equipment
    }
  }, [open, fetchDepartmentEquipment]);

  // Group equipment by room
  const equipmentByRoom = useMemo(() => {
    const grouped = {};
    
    equipment.forEach((eq) => {
      const roomKey = eq.room_id || 'NO_ROOM';
      if (!grouped[roomKey]) {
        grouped[roomKey] = {
          room: eq.room || null,
          equipment: []
        };
      }
      grouped[roomKey].equipment.push(eq);
    });
    
    return grouped;
  }, [equipment]);

  const handleRoomExpand = (roomId) => {
    setExpandedRoom(expandedRoom === roomId ? null : roomId);
  };

  const handleEquipmentSelect = (eq) => {
    setSelectedEquipments((prev) => {
      const isSelected = prev.some((s) => s.equipment_id === eq.equipment_id);
      return isSelected
        ? prev.filter((s) => s.equipment_id !== eq.equipment_id)
        : [...prev, eq];
    });
  };

  const handleSubmit = () => {
    if (selectedEquipments.length === 0) return;

    onSubmit({
      equipments: selectedEquipments.map((eq) => eq.equipment_id),
      relation_type: relationType,
      notes: notes || null,
      rotation_label: rotationEnabled ? (rotationLabel.trim() || null) : null,
      rotation_options: rotationEnabled && rotationOptions.length > 0 ? rotationOptions : null,
    });
  };

  const handleClose = () => {
    setSelectedEquipments([]);
    setRelationType('KONSTANS');
    setNotes('');
    setExpandedRoom(null);
    setRotationEnabled(false);
    setRotationLabel('');
    setRotationOptions([]);
    onClose();
  };

  const isEquipmentLinked = (equipmentId) => {
    return linkedEquipmentIds.includes(equipmentId);
  };

  const roomGroups = Object.entries(equipmentByRoom)
  .sort((v1, v2) => {
    const name1 = v1[1]?.room?.name || "";
    const name2 = v2[1]?.room?.name || "";

    return name1.localeCompare(name2);
  }
);
  console.log("ROOMGROUP", roomGroups)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('tasks:equipment.add_equipment_title', { defaultValue: 'Add Equipment to Task' })}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submitError && (
            <Alert severity="error">{submitError}</Alert>
          )}

          {/* Selected Equipment Display */}
          {selectedEquipments.length > 0 && (
            <Alert
              severity="info"
              onClose={() => setSelectedEquipments([])}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2">
                <strong>{t('common:messages.selected')}:</strong>{' '}
                {selectedEquipments.map((eq) => eq.name).join(', ')}
              </Typography>
            </Alert>
          )}

          {/* Room/Equipment Selector */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2">
                {t('tasks:equipment.select_equipment', { defaultValue: 'Select Equipment from Rooms' })}
              </Typography>
            </Box>
            <Divider />
            
            {equipmentLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : roomGroups.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="info">
                  {t('equipment:no_equipment_found', { defaultValue: 'No equipment found' })}
                </Alert>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {roomGroups.map(([roomKey, { room, equipment: roomEquipment }]) => {
                  const isExpanded = expandedRoom === roomKey;
                  const roomName = room?.name || t('rooms:no_room', { defaultValue: 'No Room' });

                  return (
                    <Accordion
                      key={roomKey}
                      expanded={isExpanded}
                      onChange={() => handleRoomExpand(roomKey)}
                      disableGutters
                      elevation={0}
                      sx={{ 
                        '&:before': { display: 'none' },
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                          <RoomIcon color="action" />
                          <Typography variant="body1" fontWeight="medium">
                            {roomName}
                          </Typography>
                          {room?.room_number && (
                            <Chip 
                              label={room.room_number} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                          <Box flex={1} />
                          <Chip 
                            label={`${roomEquipment.length} ${t('equipment:items', { defaultValue: 'items' })}`}
                            size="small" 
                            variant="outlined"
                            sx={{ mr: 2 }}
                          />
                        </Box>
                      </AccordionSummary>
                      
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense disablePadding>
                          {roomEquipment.map((eq) => {
                            const isLinked = isEquipmentLinked(eq.equipment_id);
                            const isSelected = selectedEquipments.some((s) => s.equipment_id === eq.equipment_id);

                            return (
                              <ListItem
                                key={eq.equipment_id}
                                disablePadding
                                disabled={isLinked}
                              >
                                <ListItemButton
                                  onClick={() => handleEquipmentSelect(eq)}
                                  selected={isSelected}
                                  disabled={isLinked}
                                >
                                  <ListItemIcon sx={{ minWidth: 0 }}>
                                    <Checkbox
                                      edge="start"
                                      checked={isSelected}
                                      disabled={isLinked}
                                      tabIndex={-1}
                                      disableRipple
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="stretch">
                                        {getEquipmentImageByModel(eq.model) ? (
                                          <Box
                                            sx={{
                                              width: 90,
                                              backgroundImage: `url(${getEquipmentImageByModel(eq.model)})`,
                                              backgroundSize: 'contain',
                                              backgroundPosition: 'center',
                                              backgroundRepeat: 'no-repeat',
                                              borderRadius: 1,
                                            }}
                                          />
                                        ) : (
                                          <Box width="90px" display="flex">
                                            {/* <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                              <DevicesIcon fontSize="small" />
                                            </Avatar> */}
                                          </Box>
                                        )}
                                        <Box display="flex" flexDirection="column">
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 20 }}>
                                            <Typography variant="body2">
                                              {eq.name}
                                            </Typography>
                                            {eq.is_active ? (
                                              <CheckCircleIcon fontSize="small" color="success" />
                                            ) : (
                                              <BlockIcon fontSize="small" color="warning" />
                                            )}
                                            {isLinked && (
                                              <Chip 
                                                label={t('tasks:equipment.already_linked', { defaultValue: 'Already linked' })}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                              />
                                            )}
                                          </Box>
                                          <Box>
                                            <Typography variant='caption' color='primary.light'>
                                              {t(`equipment:equipment_types.${eq.equipment_type}`)}
                                              {eq.model && ` • ${eq.model}`}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                    }
                                    secondary={
                                      <>
                                      </>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Relation Type */}
          <TextField
            select
            label={t('tasks:equipment.relation_type', { defaultValue: 'Relation Type' })}
            value={relationType}
            onChange={(e) => setRelationType(e.target.value)}
            required
            disabled={submitLoading}
          >
            {RELATION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {t(`equipment:relation_types.${type.value}`)}
              </MenuItem>
            ))}
          </TextField>

          {/* Rotating value (applies to every equipment selected above) */}
          <RotationOptionsEditor
            enabled={rotationEnabled}
            onEnabledChange={setRotationEnabled}
            rotationLabel={rotationLabel}
            onRotationLabelChange={setRotationLabel}
            rotationOptions={rotationOptions}
            onRotationOptionsChange={setRotationOptions}
            disabled={submitLoading}
          />

          {/* Notes */}
          <TextField
            label={t('common:labels.notes')}
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('tasks:equipment.notes_placeholder', { 
              defaultValue: 'Optional notes about this relationship...' 
            })}
            inputProps={{ maxLength: 500 }}
            helperText={`${notes.length}/500 ${t('common:labels.characters', { defaultValue: 'characters' })}`}
            disabled={submitLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitLoading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={selectedEquipments.length === 0 || submitLoading}
          startIcon={submitLoading && <CircularProgress size={16} />}
        >
          {submitLoading ? t('common:labels.adding') : t('common:buttons.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}